import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getScheduledProductsByProductCode } from "../../api/action/products";
import FormatPrice from "../../components/FormatPrice";
import { useCart } from "../../context/CartContext";

import { addShoppingCartItems } from "../../api/action/user.jsx";
import { Alert as UiAlert } from "../../components/ui/Feedback";
import ScheduleCards from "./ScheduleCards";
const ProdDetailsContext = createContext(null);
export const ProdDetailsProvider = ProdDetailsContext.Provider;
export const useProdDetails = () => {
  return useContext(ProdDetailsContext);
};

const ProductDetails = ({ previewProduct = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [scheduledProduct, setScheduledProduct] = useState([]);
  const selectedProduct = useSelector((state) => state.selectedProduct.selectedProduct);
  const storedProduct = previewProduct || selectedProduct;
  const userAccount = useSelector((state) => state?.auth?.data);
  const domain = localStorage.getItem("domainType");
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [productDetails, setProductDetails] = useState("");
  const [quantity, setQuantity] = useState(1);
  const existingProducts = JSON.parse(localStorage.getItem("cartProducts"));
  const { addToCart } = useCart();
  const companyDetail = useSelector((state) => state.companyReducer.company)
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  useEffect(() => {
    const prodCode = storedProduct?.primaryId;
    if (domain !== "RETAIL" && prodCode) {
      dispatch(getScheduledProductsByProductCode(prodCode))
        .then((response) => {
          setScheduledProduct(response);
        })
        .catch(() => {
          setAlertMsg({
            message: "Unable to load schedule details. Please try again.",
            open: true,
            severity: "error",
          });
        });
    }

    return () => {
      setScheduledProduct([]);
    };
  }, [dispatch, domain, storedProduct?.primaryId, userAccount]);

  const handleClose = (...closeArgs) => {
    const reason = closeArgs[1];
    if (reason === "clickaway") {
      return;
    }
    setAlertMsg((current) => ({ ...current, open: false }));
  };

  useEffect(() => {
    if (!alertMsg.open) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAlertMsg((current) => ({ ...current, open: false }));
    }, 6000);

    return () => window.clearTimeout(timeoutId);
  }, [alertMsg.open, alertMsg.message, alertMsg.severity]);

  useEffect(() => {
    setProductDetails(storedProduct || "");

    return () => {
      setProductDetails([]);
    };
  }, [domain, storedProduct]);

  const addToShoppingCart = async (cartItem) => {
    const shoppingCartId = userAccount?.account?.shoppingCart?.primaryId;
    if (!shoppingCartId) {
      setAlertMsg({
        message: "Shopping cart ID is not available. Please refresh the page.",
        open: true,
        severity: "error",
      });
      return;
    }

    const payload = {
      primaryId: shoppingCartId,
      accountId: userAccount.account.primaryId,
      createdBy: "WEB_UI",
      modifiedBy: "WEB_UI",
      productList: [cartItem],
      company: {
        primaryId: companyDetail?.primaryId,
      },
    };


    try {
      const response = await addShoppingCartItems(payload);
      if (response) {
        navigate("/cart");
      }
    } catch (error) {
      setAlertMsg({
        message: error.message,
        open: true,
        severity: "error",
      });
    }
  };

  const handleCart = async (productDetails) => {
    if (domain === "RETAIL") {
      const isProductInCart = (existingProducts || []).some(
        (item) => item.product.primaryId === productDetails.primaryId
      );
      if (!isProductInCart) {
        const retailReq = {
          quantity: quantity,
          // totalPrice: productDetails.price.priceAmt,
          product: {
            deleted: productDetails.deleted,
            name: productDetails.name,
            price: {
              priceAmt: productDetails?.price?.priceAmt,
            },
            code: productDetails.code,
            _id: productDetails._id,
            primaryId: productDetails.primaryId,
            bulkOrderEnabled: Boolean(productDetails.bulkOrderEnabled),
          },
        };
        addToShoppingCart(retailReq);
        addToCart(retailReq);
      } else {
      }
    } else {
      const selectedScheduledProduct = selectedSchedule
        ? JSON.parse(selectedSchedule)
        : scheduledProduct[0];

      if (!selectedScheduledProduct?.primaryId) {
        setAlertMsg({
          message: "Please select a schedule before adding this course to the cart.",
          open: true,
          severity: "error",
        });
        return;
      }

      const isProductInCart = (existingProducts || []).some(
        (item) =>
          item.scheduledProduct?.primaryId === selectedScheduledProduct.primaryId
      );
      if (!isProductInCart) {
        const schduledReq = {
          quantity: quantity,
          // totalPrice: productDetails.price.priceAmt,
          scheduledProduct: {
            deleted: selectedScheduledProduct.deleted,
            hasSchedule: selectedScheduledProduct.hasSchedule,
            status: selectedScheduledProduct.status,
            product: {
              deleted: productDetails.deleted,
              name: productDetails.name,
              code: productDetails.code,
              _id: productDetails._id,
              primaryId: productDetails.primaryId,
            },
            schedule: {
              deleted: selectedScheduledProduct.schedule?.deleted,
              scheduleCode: selectedScheduledProduct.schedule?.scheduleCode,
            },
            _id: selectedScheduledProduct._id,
            primaryId: selectedScheduledProduct.primaryId,
          },
        };
        addToShoppingCart(schduledReq);
        addToCart(schduledReq);
      } else {
        setAlertMsg({
          message: "This schedule is already in the cart",
          open: true,
          severity: "error",
        });
        return;
      }
    }
  };

  const incrementQuantity = () => {
    if (quantity === 10) {
      setAlertMsg({
        message: "Quantity cannot be more than 10",
        open: true,
        severity: "warning",
      });
      setQuantity(10);
    } else {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleScheduleChange = (event) => {
    setSelectedSchedule(event.target.value);
  };

  const [selectedValues, setSelectedValues] = useState({});
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    if (
      productDetails?.imageUrlsList &&
      productDetails.imageUrlsList.length > 0
    ) {
      setMainImage(productDetails.imageUrlsList[0]);
    }
  }, [productDetails]);

  return (
    <>
      <div id="product-details-wrapper" className="sp mx-auto max-w-7xl px-2 py-10 lg:px-0">
        <div className="overflow-hidden">
          <div className="mb-9 pt-4 md:px-6 md:pt-7 lg:mb-2 lg:p-8 2xl:p-10 2xl:pt-10">
            {/* Thumbnail Preview */}
            <div id="thumbnail-list" className="flex flex-wrap justify-start mb-4 overflow-x-auto max-w-full">
              {productDetails?.imageUrlsList &&
                productDetails.imageUrlsList.map((url, index) => (
                  <img
                    loading="lazy"
                    key={index}
                    src={url}
                    alt={`${productDetails?.name || "Product"} thumbnail ${index + 1}`}
                    className="h-16 w-16 object-cover rounded-md m-1 border border-gray-300 shadow-sm cursor-pointer"
                    onMouseEnter={() => {
                      setMainImage(url);
                    }}
                    id={`thumbnail-${index}`}
                  />
                ))}
            </div>

            <div className="items-start justify-between lg:flex lg:space-x-8">
              {/* Main Image */}
              <div className="mb-4 items-center justify-center overflow-hidden md:mb-8 lg:mb-0 xl:flex">
                <div className="w-full xl:flex xl:flex-row-reverse">
                  <div
                    className="relative mb-2.5 w-full shrink-0 overflow-hidden rounded-md border md:mb-3 xl:w-[480px] 2xl:w-[650px] lg:h-[468px] h-[300px]"
                    id="main-image-container"
                  >
                    <div className="relative flex items-center justify-center h-full">
                      <img
                        loading="lazy"
                        src={mainImage}
                        alt={productDetails?.name}
                        width={650}
                        height={590}
                        className="rounded-lg object-contain md:h-[300px] md:w-full scale-75 lg:h-full"
                        id="main-product-image"
                      />
                    </div>
                    <div className="absolute top-2/4 z-10 flex w-full items-center justify-between">
                      <ChevronLeft className="text-white" />
                      <ChevronRight className="text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section: Title, Price, Schedule, Add to Cart */}
              <div className="flex shrink-0 flex-col lg:w-[430px] xl:w-[470px] 2xl:w-[480px] capitalize">
                <div className="pb-2 text-center">
                  <h2 id="product-title" className="text-lg font-semibold md:text-xl xl:text-2xl text-black">
                    {productDetails?.name}
                  </h2>
                  <p className="mt-4 font-semibold text-xl" id="product-price">
                    {productDetails?.price && (
                      <FormatPrice
                        code={productDetails.price.currency || "USD"}
                        price={productDetails.price.priceAmt}
                      />
                    )}
                  </p>
                </div>

                {/* Schedule for Non-Retail */}
                {domain !== "RETAIL" && (
                  <ProdDetailsProvider value={{ handleScheduleChange, selectedSchedule }}>
                    <ScheduleCards
                      handleScheduleChange={handleScheduleChange}
                      scheduleData={scheduledProduct}
                      selectedSchedule={selectedSchedule}
                    />
                  </ProdDetailsProvider>
                )}

                {/* Quantity & Add to Cart for RETAIL */}
                <div
                  id="cart-section"
                  className="space-s-4 3xl:pr-48 flex items-center gap-6 border-b border-gray-300 py-8 md:pr-32 lg:pr-12 2xl:pr-12"
                >
                  {domain === "RETAIL" && (
                    <div className="group flex h-11 items-center overflow-hidden rounded-md border-2 border-gray-300 md:h-12">
                      <button
                        id="decrement-quantity"
                        className="text-heading hover:bg-heading h-full w-10 border-e border-gray-300"
                        disabled={quantity === 1}
                        onClick={decrementQuantity}
                      >
                        -
                      </button>
                      <span
                        id="product-quantity"
                        className="text-heading h-full w-10 flex items-center justify-center text-base font-semibold md:w-20 xl:w-24"
                      >
                        {quantity}
                      </span>
                      <button
                        id="increment-quantity"
                        className="text-heading hover:bg-heading h-full w-10 border-s border-gray-300"
                        onClick={incrementQuantity}
                      >
                        +
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    id="add-to-cart-button"
                    onClick={() => handleCart(productDetails)}
                    className="h-12 w-full rounded-full bg-[#f4a649] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#e0872d]"
                  >
                    Add to cart
                  </button>
                </div>

                {/* Description */}
                <div className="pt-6 xl:pt-8">
                  <details className="rounded-md border border-gray-200 bg-white" open>
                    <summary
                      className="flex cursor-pointer list-none items-center justify-between gap-3 p-4"
                      id="description-summary"
                    >
                      <h3 className="text-15px mb-0 font-semibold sm:text-base">Description:</h3>
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    </summary>
                    <div className="border-t border-gray-100 p-4" id="product-description">
                      <p>{productDetails?.description}</p>
                    </div>
                  </details>
                </div>

                {/* Optional Attributes */}
                {productDetails.optionalAttributes?.length > 0 && (
                  <div className="pt-6 xl:pt-8" id="product-attributes">
                    <h3 className="text-15px mb-3 font-semibold sm:text-base lg:mb-3.5">
                      Additional Informations:
                    </h3>
                    {productDetails.optionalAttributes.map((attribute, index) => (
                      <div key={index} className="mb-4" id={`attribute-${attribute.name}`}>
                        <h4 className="font-semibold">
                          {attribute.name}:
                          <span style={{ fontWeight: "normal", display: "flex", gap: "8px" }}>
                            {attribute.values.map((value, idx) => (
                              <React.Fragment key={idx}>
                                {value?.imgLocation ? (
                                  <img
                                    loading="lazy"
                                    src={value.imgLocation}
                                    alt={value.value}
                                    className="w-12 h-12 border border-gray-300 rounded-full cursor-pointer"
                                    title={value.value}
                                    onClick={() =>
                                      setSelectedValues({
                                        ...selectedValues,
                                        [attribute.name]: value.value,
                                      })
                                    }
                                    id={`attribute-${attribute.name}-${value.value}`}
                                  />
                                ) : (
                                  <span>{value?.value}</span>
                                )}
                              </React.Fragment>
                            ))}
                          </span>
                        </h4>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {alertMsg.open && (
          <div
            aria-live="polite"
            className="fixed left-1/2 top-5 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2"
            role="status"
          >
            <div className="relative" id="product-snackbar">
              <UiAlert tone={alertMsg.severity || "info"} className="pr-10 shadow-lg">
                {alertMsg.message}
              </UiAlert>
              <button
                type="button"
                aria-label="Close notification"
                className="absolute right-3 top-3 rounded p-1 text-current transition hover:bg-black/10"
                onClick={handleClose}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

};

export default ProductDetails;
